import { useEffect, useState } from 'react';
import {
  client,
  isPurchaseTrackFromPayment,
  type PaymentContextSourceType,
  type Song,
  type Tariff,
} from '../../../../api/client';
import { getCachedTariffs, refreshTariffs } from '../../../../api/tariffsCache';
import { getTrackTitle } from '../../../lib/constants.js';
import { translateApiError } from '../../../lib/errors';
import { AudioPlayer } from '../../../player/AudioPlayer';
import {
  findAdditionalGenerationTariff,
  formatAdditionalGenerationPrice,
} from './trackCardUtils';

interface Props {
  song: Song;
  playingTrack: string | null;
  setPlayingTrack: (id: string | null) => void;
  tracksBalance: number;
  onReloadData: () => Promise<void>;
  onPaymentStarted: () => void;
  onPaymentFinished: () => void;
  onError: (message: string) => void;
  onBeforePayment?: () => Promise<boolean>;
  paymentContextSource?: PaymentContextSourceType;
  showRegenerate?: boolean;
  onRegenerate?: (songId: string) => Promise<void>;
  additionalGenerationBalance: number;
}

const ACTION_BUTTON =
  'w-full h-12 px-6 rounded-2xl text-base leading-6 font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed';

function triggerDownload(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Renders tracks, unlock actions, and the additional-generation purchase/generation action. */
export function TrackCard({
  song,
  playingTrack,
  setPlayingTrack,
  tracksBalance,
  onReloadData,
  onPaymentStarted,
  onPaymentFinished,
  onError,
  onBeforePayment,
  paymentContextSource = 'MY_SONGS',
  showRegenerate = false,
  onRegenerate,
  additionalGenerationBalance,
}: Props) {
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const additionalGenerationParams = {
    include_items: ['ADDITIONAL_GENERATION'] as const,
    limit: 50,
    offset: 0,
  };
  const cachedAdditionalGeneration = getCachedTariffs(additionalGenerationParams);
  const [additionalGenerationTariff, setAdditionalGenerationTariff] =
    useState<Tariff | null>(() =>
      cachedAdditionalGeneration
        ? findAdditionalGenerationTariff(cachedAdditionalGeneration.tariffs)
        : null,
    );
  const [additionalGenerationLoading, setAdditionalGenerationLoading] =
    useState(!cachedAdditionalGeneration);
  const [additionalGenerationBuying, setAdditionalGenerationBuying] =
    useState(false);

  const allUnlocked = song.isUnlocked;

  useEffect(() => {
    let mounted = true;

    void refreshTariffs(additionalGenerationParams)
      .then((response) => {
        if (!mounted) return;
        setAdditionalGenerationTariff(
          findAdditionalGenerationTariff(response.tariffs),
        );
      })
      .catch(() => {
        if (mounted && !cachedAdditionalGeneration) {
          setAdditionalGenerationTariff(null);
        }
      })
      .finally(() => {
        if (mounted) setAdditionalGenerationLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handlePurchase = async () => {
    try {
      if (onBeforePayment && !(await onBeforePayment())) return;
      onPaymentStarted();
      const response = await client.songs.purchase(song.id, {
        source: 'payment',
        context: { source: paymentContextSource },
      });
      if (isPurchaseTrackFromPayment(response)) {
        window.location.href = response.paymentUrl;
      } else {
        await onReloadData();
      }
    } catch (error) {
      onError(translateApiError(error));
    } finally {
      onPaymentFinished();
    }
  };

  const handlePurchaseViaBalance = async () => {
    try {
      onPaymentStarted();
      const response = await client.songs.purchase(song.id, {
        source: 'balance',
        context: { source: paymentContextSource },
      });
      await onReloadData();
    } catch (error) {
      onError(translateApiError(error));
    } finally {
      onPaymentFinished();
    }
  };

  const handlePurchaseAdditionalGeneration = async (): Promise<void> => {
    if (!additionalGenerationTariff || additionalGenerationBuying) return;

    try {
      if (onBeforePayment && !(await onBeforePayment())) return;
      setAdditionalGenerationBuying(true);
      onPaymentStarted();
      const response = await client.payments.purchaseGeneration({
        tariffId: additionalGenerationTariff.id,
        context: { source: paymentContextSource },
      });
      window.location.href = response.paymentUrl;
    } catch (error) {
      onError(translateApiError(error));
      onPaymentFinished();
      setAdditionalGenerationBuying(false);
    }
  };

  const handleGenerateAdditional = async (): Promise<void> => {
    if (!onRegenerate || regenerateLoading) return;
    setRegenerateLoading(true);
    try {
      await onRegenerate(song.id);
    } catch {
      // Parent already surfaced the API error.
    } finally {
      setRegenerateLoading(false);
    }
  };

  const handleDownloadArchive = async () => {
    setArchiveLoading(true);
    try {
      const { downloadUrl } = await client.songs.getCompressedTracks(song.id);
      triggerDownload(downloadUrl);
    } catch (error) {
      onError(translateApiError(error));
    } finally {
      setArchiveLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="rounded-2xl bg-white/80 p-6 shadow-xl backdrop-blur-md">
        <div className="flex flex-col gap-10">
          <div>
            {song.tracks.map((track, index) => (
              <div
                key={track.trackId}
                className="py-7 first:pt-0 last:pb-0 border-b border-gray-200 last:border-b-0"
              >
                <h3 className="mb-4 text-xl leading-7 font-bold text-gray-900">
                  {getTrackTitle(index)}
                </h3>
                <AudioPlayer
                  trackId={track.trackId}
                  src={track.previewUrl}
                  playingTrack={playingTrack}
                  setPlayingTrack={setPlayingTrack}
                />
              </div>
            ))}
          </div>

          {allUnlocked ? (
            <button
              type="button"
              onClick={handleDownloadArchive}
              disabled={archiveLoading}
              className={ACTION_BUTTON}
            >
              {archiveLoading ? 'Готовим архив…' : 'Скачать песни'}
            </button>
          ) : tracksBalance > 0 ? (
            <button
              type="button"
              onClick={handlePurchaseViaBalance}
              className={ACTION_BUTTON}
            >
              Выбрать эти песни
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePurchase}
              className={ACTION_BUTTON}
            >
              Купить за 199 ₽
            </button>
          )}
        </div>
      </div>

      {showRegenerate &&
        additionalGenerationTariff &&
        !additionalGenerationLoading &&
        (additionalGenerationBalance === 0 || Boolean(onRegenerate)) && (
          <button
            type="button"
            onClick={
              additionalGenerationBalance > 0
                ? handleGenerateAdditional
                : handlePurchaseAdditionalGeneration
            }
            disabled={
              additionalGenerationBalance > 0
                ? regenerateLoading
                : additionalGenerationBuying
            }
            className="mt-4 flex min-h-20 w-full items-center justify-center rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 text-center text-base font-bold leading-4 text-white shadow-xl transition-all hover:brightness-105 sm:min-h-12 sm:px-6 sm:leading-6"
          >
            {additionalGenerationBalance > 0
              ? regenerateLoading
                ? 'Запускаем генерацию…'
                : 'Сгенерировать дополнительные варианты'
              : additionalGenerationBuying
                ? 'Переходим к оплате…'
                : `Не совсем то, что вы ожидали услышать? Сгенерировать дополнительные варианты за ${formatAdditionalGenerationPrice(additionalGenerationTariff.price.amount)} ₽`}
          </button>
        )}
    </div>
  );
}
